import { EREFERRALS_EXPERTISE_CODES } from '@libs/types/src/services/types/service-settings-ereferrals.type';
import { AfterViewInit, Component, inject, OnDestroy, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { ServicesSettingsPageStore } from './services-settings-page.store';
import { Router, RouterOutlet } from '@angular/router';
import { LayoutService } from '../../../../layout/services/layout.service';
import { selectService } from '../../../store/service/service.selectors';
import { isFolderStatusAllowed } from '../../../../../../../../types/src/services/enum/folder-status.enum';
import {
    ServiceTypeEnum,
    serviceTypeNameAllowed,
} from '../../../../../../../../types/src/services/enum/service-type.enum';
import { serviceUri } from 'packages/client/src/app/utils/router.utils';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { Ripple } from '@openng/optimus-ui/ripple';
import { featureOn } from '../../../../../feature-flag';
import { workTypeAllowedExpertises } from '@libs/types/src/services/enum/work-type.enum';
import { Operations } from '@it2go/types';
import { GlobalStore } from '../../../../../store/global.global-store';
import {
    servicesWithAllowedProvidedOperationsReports,
} from '../../../../../components/social-service/provided-operations-reports/provided-operations-reports.utils';
import { ServicesSettingsPageStore2 } from './services-settings-page.store2';

import { NURSING_ALLOWANCE_EXPERTISE_CODES } from '@libs/types/src/services/utils/nursing-allowance';
import { isFreeService } from '../../../../../utils/service.utils';
@Component({
    selector: 'app-services-settings-page',
    templateUrl: 'services-settings-page.component.html',
    styleUrls: ['services-settings-page.component.scss'],
    providers: [
        ServicesSettingsPageStore,
        ServicesSettingsPageStore2,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        RouterOutlet,
        AsyncPipe,
        NgTemplateOutlet,
        TranslatePipe,
        Ripple,
        NgClass,
    ],
})
export class ServicesSettingsPageComponent extends SubscriptionComponent implements AfterViewInit, OnDestroy {

    protected readonly store = inject(ServicesSettingsPageStore);
    private readonly router = inject(Router);
    private readonly layoutService = inject(LayoutService);

    @ViewChild('subsubmenuContent')
    subsubmenuContent: TemplateRef<unknown> | null = null;

    protected globalGlobalStore = inject(GlobalStore);
    protected store2 = inject(ServicesSettingsPageStore2);

    itemsFirstGroup: string[] = [];
    itemsSecondGroup: string[] = [];
    itemsThirdGroup: string[] = [];

    constructor() {
        super();
        const isAllowed = (operation: Operations) => this.globalGlobalStore.isAllowed(operation);
        this.subs.push(
            this.store.globalStore!.select(selectService).subscribe((service) => {
                const expertiseCode = service?.expertise?.code || '';
                const allowed = isFolderStatusAllowed(expertiseCode);
                const isHealthCare = service?.type === ServiceTypeEnum.HEALTH_CARE;
                const isSocial = service?.type === ServiceTypeEnum.SOCIAL;

                this.itemsFirstGroup = ['general', 'dashboard'];
                this.itemsSecondGroup = ['contacts'];
                this.itemsThirdGroup = ['documents', 'risk-type'];

                if (isHealthCare || (isSocial && featureOn.SOCIAL_STATISTICS)) {
                    this.itemsThirdGroup.push('statistics');
                }

                featureOn.NEW_CALENDAR && this.itemsFirstGroup.push('calendar');

                isHealthCare && this.itemsSecondGroup.push(
                    'forms',
                );

                if (isHealthCare && allowed.voucherAllowed) {
                    this.itemsSecondGroup.push(
                        'voucher',
                    );
                }

                if (isHealthCare && allowed.anamnesisAllowed) {
                    this.itemsSecondGroup.push(
                        'anamnesis',
                    );
                }

                if (isHealthCare && allowed.carePlanAllowed) {
                    this.itemsSecondGroup.push(
                        'care-plan',
                    );
                }

                isHealthCare && this.itemsSecondGroup.push(
                    'medication',
                );

                if (isHealthCare && allowed.ePrescriptionAllowed) {
                    this.itemsSecondGroup.push(
                        'eprescriptions',
                    );
                }

                if (featureOn.E_POUKAZ && isHealthCare && allowed.ePoukazAllowed) {
                    this.itemsSecondGroup.push('epoukaz');
                }

                if (isHealthCare && allowed.sickNoteAllowed) {
                    this.itemsSecondGroup.push(
                        'sick-notes',
                    );
                }

                if (featureOn.NURSING_ALLOWANCE && isHealthCare
                    && NURSING_ALLOWANCE_EXPERTISE_CODES.includes(expertiseCode)) {
                    this.itemsSecondGroup.push('nursing-allowance');
                }

                if (isHealthCare && allowed.epicrisisAllowed) {
                    this.itemsSecondGroup.push(
                        'epicrisis',
                    );
                }

                featureOn.SOCIAL_PAYMENTS_AND_BILLING && isSocial && this.itemsSecondGroup.push(
                    'social-payment',
                );

                featureOn.SOCIAL_FINANCIAL_DEPOSIT && isSocial && this.itemsSecondGroup.push(
                    'financial-deposit',
                );

                isHealthCare && this.itemsSecondGroup.push(
                    'nurse-record',
                    'visit-type',
                );
                isSocial && this.itemsSecondGroup.push(
                    'individual-plan',
                );
                isSocial && this.itemsSecondGroup.push(
                    'contracts',
                );
                featureOn.ATTENDANCE && isSocial && this.itemsSecondGroup.push(
                    'attendance',
                );
                featureOn.WAREHOUSE_MATERIAL && isSocial && this.itemsSecondGroup.push(
                    'warehouse-material',
                );
                featureOn.SERVICE_ANNOUNCEMENT && isSocial && this.itemsSecondGroup.push(
                    'service-announcements',
                );
                featureOn.TANGIBLE_DEPOSITS && isSocial && this.itemsSecondGroup.push(
                    'tangible-deposits',
                );
                isSocial && this.itemsSecondGroup.push(
                    'continuous-records',
                    'movement-restriction',
                    'sanction',
                    'user-tasks',
                );
                if (serviceTypeNameAllowed.diets.includes(service?.serviceType?.name || '') && isAllowed('serviceDiet.update') && isSocial) {
                    this.itemsSecondGroup.push('diet-setting');
                }
                featureOn.SUGGESTION && isSocial && this.itemsSecondGroup.push(
                    'suggestion',
                );

                if (service) {
                    if (workTypeAllowedExpertises.doctorRecords.includes(expertiseCode)) {
                        this.itemsSecondGroup.push('doctor-record');
                    }
                    if (workTypeAllowedExpertises.specialistRecords.includes(expertiseCode)) {
                        this.itemsSecondGroup.push('specialist-record');
                    }
                }

                this.itemsSecondGroup.push('operational-reports');

                featureOn.SOCIAL_JANUARY && isSocial && this.itemsSecondGroup.push('mail');

                if (isHealthCare) {
                    this.itemsThirdGroup = ['risk-type', 'measured-values', 'scheduler', 'statistics', 'documents'];
                }

                isAllowed('work.update') && isSocial && this.itemsThirdGroup.push(
                    'social-works',
                );

                isAllowed('basicSocialConsulting.update') && isSocial && this.itemsThirdGroup.push(
                    'basic-social-consulting',
                );

                const dashboardSettings = this.store2.socialServicePaymentSetting();
                featureOn.SOCIAL_PAYMENTS_AND_BILLING && isSocial && (dashboardSettings?.useCard ?? true) && this.itemsThirdGroup.push(
                    'social-billing',
                );

                featureOn.PROVIDED_OPERATIONS_REPORTS &&
                isSocial &&
                servicesWithAllowedProvidedOperationsReports([service]).length &&
                this.itemsThirdGroup.push(
                    'provided-operations-reports',
                );

                featureOn.SOCIAL_CREDITS &&
                isSocial &&
                isFreeService(service.serviceType?.settings) &&
                this.itemsSecondGroup.push('credits');

                if (featureOn.EREFERRALS && isHealthCare && EREFERRALS_EXPERTISE_CODES.includes(expertiseCode)) {
                    this.itemsSecondGroup.push('ereferrals');
                }
            }),
        );

        this.itemsFirstGroup = this.itemsFirstGroup.sort();
        this.itemsSecondGroup = this.itemsSecondGroup.sort();
        this.itemsThirdGroup = this.itemsThirdGroup.sort();
    }

    ngAfterViewInit(): void {
        this.layoutService.setSubsubmenuContent(this.subsubmenuContent);
    }

    async itemOnClick(path: string) {
        const serviceId = this.store.routeParam.serviceId.value;
        await this.router.navigateByUrl(serviceUri(serviceId, `settings/${path}`));
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.layoutService.setSubsubmenuContent(null);
    }
}
